import { InputAction, INPUT_CONFIG } from './constants';

/**
 * Input handler for keyboard and touch controls
 * Note: Mobile and desktop have different camera angles, so the
 * visual left/right direction differs. This is handled by using
 * separate input mappings for each control type.
 */
export class Input {
  private keys: Set<string> = new Set();
  private actionQueue: InputAction[] = [];

  constructor() {
    this.setupKeyboardListeners();
    this.setupTouchListeners();
  }

  // ===========================================================================
  // Keyboard Controls (Desktop - angled camera)
  // On desktop, the camera is angled, so we need to swap directions
  // ===========================================================================

  private setupKeyboardListeners(): void {
    window.addEventListener('keydown', (e) => {
      if (this.keys.has(e.code)) return;

      this.keys.add(e.code);

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          this.actionQueue.push('jump');
          break;
        // Arrow keys - adjusted for desktop camera angle
        case 'ArrowUp':
        case 'KeyW':
          e.preventDefault();
          this.actionQueue.push('left');
          break;
        case 'ArrowDown':
        case 'KeyS':
          e.preventDefault();
          this.actionQueue.push('right');
          break;
        case 'ArrowLeft':
        case 'KeyA':
          e.preventDefault();
          this.actionQueue.push('right');
          break;
        case 'ArrowRight':
        case 'KeyD':
          e.preventDefault();
          this.actionQueue.push('left');
          break;
      }
    });

    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
    });
  }

  // ===========================================================================
  // Touch Controls (Mobile - behind camera)
  // On mobile, the camera is directly behind, so directions are intuitive
  // We need to swap because moveLeft() moves to negative X, which appears
  // on the RIGHT side of the screen with the mobile camera angle
  // ===========================================================================

  private setupTouchListeners(): void {
    const canvas = document.getElementById('game-canvas');
    if (!canvas) return;

    let touchStartX = 0;
    let touchStartY = 0;

    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, { passive: false });

    canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      const touchEndX = touch.clientX;
      const touchEndY = touch.clientY;

      const deltaX = touchEndX - touchStartX;
      const deltaY = touchStartY - touchEndY;

      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      const isSwipe = Math.abs(deltaX) > INPUT_CONFIG.SWIPE_THRESHOLD 
                   || Math.abs(deltaY) > INPUT_CONFIG.SWIPE_THRESHOLD;

      if (isSwipe) {
        this.handleMobileSwipe(deltaX, deltaY);
      } else {
        this.handleMobileTap(touchStartX, screenWidth, screenHeight);
      }
    }, { passive: false });

    this.setupTouchButtons();
  }

  private handleMobileSwipe(deltaX: number, deltaY: number): void {
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      // Vertical swipe
      this.actionQueue.push(deltaY > 0 ? 'left' : 'right');
    } else {
      // Horizontal swipe - swipe right = move right on screen
      this.actionQueue.push(deltaX > 0 ? 'left' : 'right');
    }
  }

  private handleMobileTap(tapX: number, screenWidth: number, screenHeight: number): void {
    const isPortrait = screenHeight > screenWidth;

    if (isPortrait) {
      const leftZone = screenWidth * INPUT_CONFIG.TAP_ZONE_LEFT;
      const rightZone = screenWidth * INPUT_CONFIG.TAP_ZONE_RIGHT;

      if (tapX < leftZone) {
        // Tap left side = move left on screen (same as left button)
        this.actionQueue.push('right');
      } else if (tapX > rightZone) {
        // Tap right side = move right on screen (same as right button)
        this.actionQueue.push('left');
      } else {
        this.actionQueue.push('jump');
      }
    } else {
      this.actionQueue.push('jump');
    }
  }

  private setupTouchButtons(): void {
    const jumpBtn = document.getElementById('touch-jump');
    const leftBtn = document.getElementById('touch-up');   // Visual left button
    const rightBtn = document.getElementById('touch-down'); // Visual right button

    if (jumpBtn) {
      jumpBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.actionQueue.push('jump');
      }, { passive: false });
    }

    // Mobile: visual left button should move character left on screen
    // Due to camera angle, this requires 'right' action
    if (leftBtn) {
      leftBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.actionQueue.push('right');
      }, { passive: false });
    }

    // Mobile: visual right button should move character right on screen
    // Due to camera angle, this requires 'left' action
    if (rightBtn) {
      rightBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.actionQueue.push('left');
      }, { passive: false });
    }
  }

  // ===========================================================================
  // Public Methods
  // ===========================================================================

  public getActions(): InputAction[] {
    const actions = [...this.actionQueue];
    this.actionQueue = [];
    return actions;
  }

  public isKeyDown(code: string): boolean {
    return this.keys.has(code);
  }

  public clear(): void {
    this.actionQueue = [];
  }
}
